import { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, Chip, CircularProgress, Alert, Grid,
  Divider, Link, Table, TableBody, TableCell, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { ArrowBack as BackIcon, Delete as DeleteIcon } from '@mui/icons-material';
import api from '../../config/api';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import { EXPORT_STATUS_COLORS, ContractTalanaChip, contractDeleteErrorMessage } from './ErpContractsLayout';
import formatRut from '../../erp/formatRut';
import { formatDateTime, formatDateRange } from '../formatDate';
import ErpContractTerminationCard from './ErpContractTerminationCard';
import ErpContractDocumentCard from './ErpContractDocumentCard';
import ErpContractSignatureCard from './ErpContractSignatureCard';

const toDateLabel = (value) => formatDateTime(value); // DD/MM/AAAA HH:mm

const Row = ({ label, value }) => (
  <Typography variant="body2" sx={{ mb: 0.5 }}>
    <strong>{label}:</strong> {value ?? '—'}
  </Typography>
);

/**
 * Detalle del contrato con la cadena trazable completa (criterio 14 de la
 * spec): cliente → cotización → ficha → contrato, con control de causal,
 * estado de export y registro de cambios post-export (decisión 6).
 */
const ErpContractDetailLayout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { contractId } = useParams();
  const { organization, authToken, showAlert } = useStore.getState();

  const [data, setData] = useState(null); // { contract, employeeRecord, quotation }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // silent: recarga sin el spinner de página (después de registrar o deshacer el término)
  const load = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const response = await api.get(`/erp/contracts/${contractId}`, {
        params: { organizationId: organization },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (response.data.response?.status_response === 'OK') {
        setData(response.data.response.data);
      } else {
        setError(t('erp.contracts.loadError'));
      }
    } catch (err) {
      console.error(err);
      setError(t('erp.contracts.loadError'));
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (organization && contractId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization, contractId]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  if (error || !data?.contract) {
    return <Box sx={{ p: 3 }}><Alert severity="error">{error || t('erp.contracts.loadError')}</Alert></Box>;
  }

  const { contract, employeeRecord, quotation } = data;
  const balance = contract.causeControl;

  const handleDelete = async () => {
    try {
      setDeleting(true);
      const response = await api.delete(`/erp/contracts/${contractId}`, {
        params: { organizationId: organization },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (response.data.response?.status_response === 'OK') {
        showAlert(t('erp.contracts.deleteSuccess'), 'success');
        navigate('/erp/contracts');
      }
    } catch (err) {
      console.error(err);
      showAlert(contractDeleteErrorMessage(t, err.response?.data?.response), 'error');
      setDeleteOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Button startIcon={<BackIcon />} onClick={() => navigate('/erp/contracts')} sx={{ textTransform: 'none', mb: 1 }}>
        {t('erp.contracts.title')}
      </Button>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>{contract.workerName || formatRut(contract.workerRut)}</Typography>
          <Typography variant="body2" color="text.secondary">
            {formatRut(contract.workerRut)} · {contract.clientName || '—'} · {formatDateRange(contract.dates?.startDate, contract.dates?.endDate, t('erp.contracts.openEnded'))}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('erp.contracts.detail.payrollKey')}: {contract.uid}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label={t(`erp.contracts.exportStatus.${contract.exportStatus}`)}
            color={EXPORT_STATUS_COLORS[contract.exportStatus] || 'default'} />
          <ContractTalanaChip talana={contract.talana} size="medium" />
          {contract.exportStatus === 'pending' && (
            <Button variant="outlined" color="error" size="small" startIcon={<DeleteIcon />}
              disabled={deleting} onClick={() => setDeleteOpen(true)} sx={{ textTransform: 'none' }}>
              {t('erp.contracts.deleteContract')}
            </Button>
          )}
        </Box>
      </Box>

      {/* Confirmación de eliminación (solo pendientes; revierte la cadena) */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('erp.contracts.deleteContract')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{t('erp.contracts.deleteConfirmText')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} sx={{ textTransform: 'none' }}>
            {t('erp.common.cancel')}
          </Button>
          <Button variant="contained" color="error" disabled={deleting} onClick={handleDelete}
            sx={{ textTransform: 'none' }}>
            {t('erp.contracts.deleteContract')}
          </Button>
        </DialogActions>
      </Dialog>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 2, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight={700}>{t('erp.contracts.detail.causeTitle')}</Typography>
            <Divider sx={{ my: 1 }} />
            <Row label={t('erp.contracts.form.hiringCause')}
              value={contract.hiringCause ? `${contract.hiringCause.code} — ${contract.hiringCause.name}` : '—'} />
            {balance && (
              <>
                <Row label={t('erp.contracts.balance.consumed')} value={balance.daysConsumed} />
                <Row label={t('erp.contracts.balance.limit')} value={balance.legalLimitDays ?? '∞'} />
                <Row label={t('erp.contracts.balance.available')} value={balance.daysAvailable ?? '∞'} />
                <Row label={t('erp.contracts.balance.renewals')} value={balance.renewals} />
                {balance.warning && (
                  <Alert severity="error" sx={{ mt: 1 }}>{t('erp.contracts.balance.warning')}</Alert>
                )}
              </>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 2, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight={700}>{t('erp.contracts.detail.chainTitle')}</Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>{t('erp.clients.title')}:</strong>{' '}
              <Link component="button" underline="hover"
                onClick={() => navigate(`/erp/clients/${contract.clientId}`)}>
                {contract.clientName || contract.clientId}
              </Link>
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>{t('erp.quotations.title')}:</strong>{' '}
              <Link component="button" underline="hover"
                onClick={() => navigate(`/erp/quotations/${contract.quotationId}`)}>
                {quotation ? `${quotation.quotationNumber ? quotation.quotationNumber + ' · ' : ''}${quotation.clientName} — ${quotation.jobTitleName}` : contract.quotationId}
              </Link>
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>{t('erp.employees.title')}:</strong>{' '}
              <Link component="button" underline="hover"
                onClick={() => navigate(`/erp/employees/${contract.employeeRecordId}`)}>
                {employeeRecord
                  ? [employeeRecord.personal?.names, employeeRecord.personal?.firstLastName].filter(Boolean).join(' ')
                  : contract.employeeRecordId}
              </Link>
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {t('erp.contracts.detail.chainNote')}
            </Typography>
          </Paper>
        </Grid>

        {/* Contrato tipo en PDF (PLAN_ERP.md §15, fase B1) */}
        <Grid item xs={12}>
          <ErpContractDocumentCard contract={contract} quotation={quotation}
            contractDocument={data.contractDocument} />
        </Grid>

        {/* Firma con Izydocs (PLAN_ERP.md §15.4, fase B2) */}
        <Grid item xs={12}>
          <ErpContractSignatureCard contract={contract} employeeRecord={employeeRecord}
            signatureCheck={data.signatureCheck} onChanged={() => load({ silent: true })} />
        </Grid>

        {/* Término del contrato y aviso de vencimiento (PLAN_ERP.md §15) */}
        <Grid item xs={12}>
          <ErpContractTerminationCard contract={contract} quotation={quotation}
            terminationNotice={data.terminationNotice} onChanged={() => load({ silent: true })} />
        </Grid>

        {(contract.changeLog || []).length > 0 && (
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 2 }}>
              <Typography variant="subtitle1" fontWeight={700}>{t('erp.contracts.detail.changeLogTitle')}</Typography>
              <Divider sx={{ my: 1 }} />
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('erp.contracts.changeLog.field')}</TableCell>
                    <TableCell>{t('erp.contracts.changeLog.oldValue')}</TableCell>
                    <TableCell>{t('erp.contracts.changeLog.newValue')}</TableCell>
                    <TableCell>{t('erp.contracts.changeLog.changedAt')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contract.changeLog.map((entry, index) => (
                    <TableRow key={index}>
                      <TableCell>{entry.field}</TableCell>
                      <TableCell>{String(entry.oldValue ?? '—')}</TableCell>
                      <TableCell>{String(entry.newValue ?? '—')}</TableCell>
                      <TableCell>{toDateLabel(entry.changedAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default ErpContractDetailLayout;
